import React, { Component } from "react";
import PropTypes from "prop-types";
import Field from "redux-form/lib/Field";
import formValueSelector from "redux-form/lib/formValueSelector";
import Immutable from "immutable";
import { connect } from "react-redux";
import { createSelector } from "reselect";
import { translate } from "react-i18next";
import isEqual from "lodash/isEqual";

import moment from "../../../lib/moment";
import { price } from "../../../lib/utils";
import SchemaField from "../../../component/SchemaField";

import "./DeliveryDetails.scss";
import Spinner from "../../../component/Spinner";

function getNextMonday(today) {
  const dayINeed = 1; // for Monday
  const todayMoment = moment(today);
  // if we haven't yet passed the day of the week that I need:
  if (todayMoment.isoWeekday() <= dayINeed) {
    // then just give me this week's instance of that day
    return todayMoment.isoWeekday(dayINeed);
  }
  // otherwise, give me next week's instance of that day
  return todayMoment.add(1, "weeks").isoWeekday(dayINeed);
}

/**
 * DeliveryDetails
 */
class DeliveryDetails extends Component {
  static propTypes = {
    shippingPrice: PropTypes.number.isRequired,
    availableTimeframes: PropTypes.instanceOf(Immutable.List).isRequired,
    schema: PropTypes.instanceOf(Immutable.Map).isRequired,
    constants: PropTypes.instanceOf(Immutable.Map).isRequired,
    context: PropTypes.instanceOf(Immutable.Map).isRequired,
    ctx: PropTypes.instanceOf(Immutable.Map).isRequired,
    ctxIdx: PropTypes.number.isRequired,
    t: PropTypes.func.isRequired,
    className: PropTypes.string,
    countryCode: PropTypes.string.isRequired,
    addressType: PropTypes.string.isRequired,
    selectedDate: PropTypes.string,
    selectedTimeFrame: PropTypes.number,
    isFetchingContext: PropTypes.bool.isRequired
  };

  static defaultProps = {
    className: "",
    selectedDate: "",
    selectedTimeFrame: 0
  };

  state = {
    isRefreshing: false
  };

  componentWillReceiveProps(props) {
    if (this.props.selectedDate !== props.selectedDate) {
      this.setState({ isRefreshing: true });
    }
    if (this.props.isFetchingContext && !props.isFetchingContext) {
      this.setState({ isRefreshing: false });
    }
  }

  shouldComponentUpdate(nextProps) {
    // for some reason this does not work in the connect() "areMergedPropsEqual" option
    return !isEqual(this.props, nextProps);
  }

  renderTimeFrameLabel = ({ title }) => {
    const titleParts = [title];
    const onlyOne = this.props.availableTimeframes.size === 1;
    const timeframe = this.props.availableTimeframes.find(t => t.get("id") === title);
    if (timeframe) {
      const isStandard = timeframe.get("id") === this.props.constants.get("ID_STANDARD");
      const isSunday = timeframe.get("id") === this.props.constants.get("ID_SUNDAY");
      const isEvening = timeframe.get("id") === this.props.constants.get("ID_EVENING");

      titleParts[0] = [
        <span className="DeliveryMoment__Title" key="Timeframe__Title">
          {timeframe.get("title")}
        </span>
      ];

      if (timeframe.get("priceEx") > 0 && !isSunday) {
        titleParts.push(
          <span className="DeliveryMoment__Price__Surcharge" key="Timeframe__Price">
            ({this.props.t("surcharge")} {price(timeframe.get("priceEx"))})
          </span>
        );
      }

      if ((timeframe.get("priceEx") === 0 && isStandard) || (onlyOne && !isSunday)) {
        titleParts.push(
          <span className="DeliveryMoment__Price" key="Timeframe__Price">
            {price(this.props.shippingPrice)}
          </span>
        );
      }

      if (timeframe.get("priceEx") === 0 && !isStandard && !onlyOne) {
        titleParts.push(
          <span className="DeliveryMoment__Price__Surcharge" key="Timeframe__Price__Surcharge">
            ({this.props.t("surcharge")} {price(timeframe.get("priceEx"))})
          </span>
        );
      }

      if (timeframe.get("priceEx") < 0 && !isStandard) {
        titleParts.push(
          <span className="DeliveryMoment__Price__Surcharge" key="Timeframe__Price__Surcharge">
            ({this.props.t("discounts")} {price(-timeframe.get("priceEx"))})
          </span>
        );
      }

      if (isSunday) {
        titleParts.push(
          <span className="DeliveryMoment__Price__Sunday" key="Timeframe__Price__Sunday">
            ({price(this.props.shippingPrice)} + {this.props.t("surcharge")} {price(timeframe.get("priceEx"))})
          </span>
        );
      }

      // if (isEvening) {
      //   titleParts.push(
      //     <div className="DeliveryMoment__Warning">
      //       {" "}
      //       * {this.props.t("Attention! no alcohol delivery in the evening")}
      //     </div>
      //   );
      // }
      return <div className="DeliveryMoment">{titleParts}</div>;
    }
    return <div className="DeliveryMoment">{titleParts}</div>;
  };

  renderDeliveryDate = props => {
    const { t, ctx, ctxIdx } = props;

    const dates = ctx.getIn(["availableDates", "dates"]);

    const availableDates = dates.map(d => moment(d));
    const firstThree = availableDates.slice(0, 3);

    const idPrefix = `form-control-recipients-${ctxIdx}-`;
    const namePrefix = `recipients[${ctxIdx}].`;

    const hasError = props.meta.error && props.meta.touched;
    return (
      <div className={hasError ? "mb-3 has-danger has-danger--bordered" : "mb-3"}>
        {props.meta.error && props.meta.touched && <div className="invalid-feedback">{props.meta.error}</div>}
        {firstThree.map(m => {
          const value = m.format("YYYY-MM-DD");
          const id = `${idPrefix}delivery-date-${value}`;
          return (
            <div key={value}>
              <label htmlFor={id} className="radioinput">
                <Field component="input" type="radio" name={`${namePrefix}delivery.date`} value={value} id={id} />
                <div className="ml-2">{m.format("dddd D MMMM")}</div>
              </label>
            </div>
          );
        })}

        <div className="mt-2 mb-3">
          <label htmlFor={`${idPrefix}delivery-date`}>
            <strong>{t("chooseDate")}</strong>
          </label>
          <Field
            component="select"
            name={`${namePrefix}delivery.date`}
            id={`${idPrefix}delivery-date`}
            className="form-control col-10"
          >
            <option />
            {availableDates.slice(3).map(m => {
              const value = m.format("YYYY-MM-DD");
              return (
                <option key={value} value={value}>
                  {m.format("dddd D MMMM")}
                </option>
              );
            })}
          </Field>
        </div>
      </div>
    );
  };

  renderHeader() {
    return <h2 className="mb-3 Cart__Step__Header">{this.props.t("shipping")}</h2>;
  }

  renderDeliveryTimeframe = props => {
    const { t, ctx, ctxIdx, availableTimeframes, schema, meta, isFetchingContext } = props;

    const namePrefix = `recipients[${ctxIdx}].`;

    const hasError = meta.touched && (meta.error || availableTimeframes.size === 0);
    return (
      <div className={hasError ? "mb-3 has-danger has-danger--bordered" : "mb-3"}>
        <h2 className="mb-3 Cart__Step__Header">
          {t("chooseTimeframe")}{" "}
          {isFetchingContext && (
            <Spinner
              radius={24}
              strokeWidth={6}
              color="#58585a"
              style={{ maxWidth: 16, maxHeight: 16, display: "inline-block" }}
            />
          )}
        </h2>
        {availableTimeframes.size === 0 && <div>{t("chooseDateBeforeTimeframe")}</div>}
        <div
          className={`Cart__Recipient__DeliveryDetails__DeliveryMoment ${
            isFetchingContext ? "Cart__Recipient__DeliveryDetails__DeliveryMoment--loading" : ""
          }`}
        >
          <SchemaField
            name={`${namePrefix}delivery.timeFrame`}
            schema={schema}
            inputType="radio"
            label={false}
            ctx={ctx}
            ctxIdx={ctxIdx}
            t={t}
            availableTimeframes={availableTimeframes}
            labelRenderer={props.labelRenderer}
          />
        </div>
      </div>
    );
  };

  renderWarning() {
    const { t, addressType, schema } = this.props;
    const NOW = moment(this.props.context.get("currentTime"));

    const timeFrameId = parseInt(this.props.selectedTimeFrame, 10);

    const isDeliveryEvening = timeFrameId === 14646028;

    // const nowTime = parseInt(NOW.format('Hmm'), 10);
    const isDeliveryToday = this.props.selectedDate === NOW.format("YYYY-MM-DD");
    const isDeliveryTomorrow =
      this.props.selectedDate ===
      NOW.clone()
        .add(1, "day")
        .format("YYYY-MM-DD");

    const hasDeliveryDate = !!this.props.selectedDate;
    if (!hasDeliveryDate) {
      return null;
    }
    const deliveryDate = moment(this.props.selectedDate);
    // eslint-disable-next-line no-unused-vars
    const isDeliveryNextMonday = deliveryDate.format("YYYY-MM-DD") === getNextMonday(NOW).format("YYYY-MM-DD");
    const isTodayFriday = NOW.day() === 5;
    const isTodaySaturday = NOW.day() === 6;
    const isTodaySunday = NOW.day() === 0;
    const isAfterTwelve = NOW.isAfter(
      moment(NOW)
        .hours(12)
        .minutes(0)
    );
    const isBeforeTwelve = !isAfterTwelve;

    const is2Company = this.props.countryCode === "NL" && addressType !== "residence";

    if (__DEV__) {
      // eslint-disable-next-line no-console
      console.log({
        NOW,
        hasDeliveryDate,
        deliveryDate,
        nowDay: NOW.day(),
        is2Company,
        isTodayFriday,
        isTodaySaturday,
        isTodaySunday,
        isDeliveryEvening,
        isDeliveryToday,
        isDeliveryTomorrow,
        isDeliveryNextMonday,
        "(isDeliveryToday && isBeforeTwelve)": isDeliveryToday && isBeforeTwelve
      });
    }

    let message = "";
    if (
      is2Company &&
      hasDeliveryDate &&
      isDeliveryEvening &&
      (isDeliveryToday ||
        isDeliveryTomorrow ||
        (isTodaySaturday && isDeliveryNextMonday) ||
        (isTodaySunday && isDeliveryNextMonday))
    ) {
      message = t("eveningDeliveryOld", {
        date:
          isDeliveryToday || isDeliveryTomorrow
            ? t(isDeliveryToday ? "today" : "tomorrow")
            : deliveryDate.format("dddd D MMMM YYYY")
      });
    }

    if (!message) {
      return null;
    }

    let addressTypeString = addressType === "other" ? t("address") : null;
    if (!addressTypeString) {
      const index = schema
        .getIn(["properties", "recipients", "items", "properties", "address", "properties", "addressType", "enum"])
        .findIndex(i => i === addressType);
      addressTypeString = schema.getIn([
        "properties",
        "recipients",
        "items",
        "properties",
        "address",
        "properties",
        "addressType",
        "enum_titles",
        index
      ]);
    }

    return (
      <div className="Cart__Recipient__DeliveryDetails__Warning">
        <i className="fa fa-exclamation-triangle mr-2" />
        <div>
          {message}.{" "}
          {t("deliveryMessage2", {
            addressType: addressTypeString.toLowerCase()
          })}
        </div>
      </div>
    );
  }

  render() {
    const { t, ctx, ctxIdx, schema, className, availableTimeframes } = this.props;

    const namePrefix = `recipients[${ctxIdx}].`;

    const dates = ctx.getIn(["availableDates", "dates"]);

    if (!dates) {
      return (
        <div className="pt-4 mt-2 pb-4 mb-3">
          <div className="alert alert-warning">{t("noDates")}</div>
        </div>
      );
    }

    if (!dates.size) {
      return (
        <div>
          {this.renderHeader()}
          <div className="alert alert-warning">{t("noDates")}</div>
        </div>
      );
    }

    const isFetchingContext = this.state.isRefreshing;

    return (
      <div className={`${className} Cart__Recipient__DeliveryDetails`}>
        {this.renderHeader()}
        {this.renderWarning()}

        <Field name={`${namePrefix}delivery.date`} component={this.renderDeliveryDate} {...{ t, ctx, ctxIdx }} />

        <Field
          name={`${namePrefix}delivery.timeFrame`}
          isFetchingContext={isFetchingContext}
          labelRenderer={this.renderTimeFrameLabel}
          component={this.renderDeliveryTimeframe}
          schema={schema}
          availableTimeframes={availableTimeframes}
          {...{ t, ctx, ctxIdx }}
        />
      </div>
    );
  }
}

const ctxSelector = createSelector(
  [(state, props) => props.ctx],
  ctx => ctx
);

const shippingPriceSelector = createSelector(
  [ctxSelector],
  ctx => ctx.getIn(["availableDates", "shippingPrice"], 0)
);

const availableTimeFramesSelector = createSelector(
  [ctxSelector],
  ctx => ctx.get("availableTimeFrames")
);

const schemaSelector = createSelector(
  [state => state.cart.get("schema"), availableTimeFramesSelector],
  (schema, availableTimeFrames) =>
    schema.setIn(
      ["properties", "recipients", "items", "properties", "delivery", "properties", "timeFrame", "enum"],
      availableTimeFrames.map(timeframe => timeframe.get("id"))
    )
);

const cartFormValueSelector = formValueSelector("cart");

export default translate()(
  connect((state, props) => ({
    addressType: cartFormValueSelector(state, `recipients[${props.ctxIdx}].address.addressType`),
    countryCode: cartFormValueSelector(state, `recipients[${props.ctxIdx}].address.countryCode`),
    selectedDate: cartFormValueSelector(state, `recipients[${props.ctxIdx}].delivery.date`),
    selectedTimeFrame: cartFormValueSelector(state, `recipients[${props.ctxIdx}].delivery.timeFrame`),
    context: state.cart.get("context"),
    isFetchingContext: state.cart.get("isFetchingContext"),
    constants: state.cart.getIn(["context", "constants"]),
    shippingPrice: shippingPriceSelector(state, props),
    availableTimeframes: availableTimeFramesSelector(state, props),
    schema: schemaSelector(state, props)
  }))(DeliveryDetails)
);
